<template>
    <div>
      <div class="col" v-if="firstLoading">
        <skeleton-loading>
          <row>
            <square-skeleton :boxProperties="{bottom: '10px', height: '800px'}"></square-skeleton>
          </row>
        </skeleton-loading>
      </div>
      <div v-else-if="listLoading" class="text-center text-primary">
        <b-spinner></b-spinner>
      </div>
      <div v-if="!listLoading && !itemCount" class="text-center text-danger">
        {{ $t('emptyList') }}
      </div>
      <div v-if="!listLoading && itemCount" class="c-template-table table-responsive">
        <table class="table table-hover">
          <thead>
          <tr>
            <th>{{ $t('callFrom') }}</th>
            <th>{{ $t('callTo') }}</th>
            <th>{{ $t('callTime') }}</th>
            <th>{{ $t('callResult') }}</th>
            <th class="th-voice">{{ $t('voiceAI') }}</th>
          </tr>
          </thead>
          <tbody v-if="items.length && !listLoading">
          <tr v-for="item in items" :key="item._id">
            <td>
              <div class="c-history-address">
                <div class="c-history-address__icon">
                  <img v-if="item.direction === 'outbound' && item.hangupCause === 'NORMAL_CLEARING'"
                       src="../../assets/images/icons/icon16-call-out-success.svg" alt="icon">
                  <img v-if="item.direction === 'inbound' && item.hangupCause === 'NORMAL_CLEARING'"
                       src="../../assets/images/icons/icon16-call-in-success.svg" alt="icon">
                  <img v-if="item.direction === 'outbound' && item.hangupCause !== 'NORMAL_CLEARING'"
                       src="../../assets/images/icons/icon16-call-out-danger.svg" alt="icon">
                  <img v-if="item.direction === 'inbound' && item.hangupCause !== 'NORMAL_CLEARING'"
                       src="../../assets/images/icons/icon16-call-in-danger.svg" alt="icon">
                  <img v-if="item.direction === 'local' && item.hangupCause === 'NORMAL_CLEARING'"
                       src="../../assets/images/icons/icon16-call-internal-success.svg" alt="icon">
                  <img v-if="item.direction === 'local' && item.hangupCause !== 'NORMAL_CLEARING'"
                       src="../../assets/images/icons/icon16-call-internal-danger.svg" alt="icon">
                </div>
                <div class="c-history-address__content">
                  <p v-if="item.fromNumber">
                    <b v-if="item.direction === 'inbound'">
                      <a @click="makeCall(item.fromNumber)" href="javascript:;"
                         v-b-tooltip.hover.top="$t('clickToCall')">
                        <phone-display :phone="item.fromNumber" :disabled="true"/>
                      </a>
                    </b>
                    <b v-else>{{ item.fromNumber }}</b>
                  </p>
                  <p class="small" v-if="item.fromExt">
                    {{ $t('ext') }}:&nbsp;<b>{{ item.fromExt }}</b>
                  </p>
                  <p v-if="item.fromUser">
                    <span class="small">{{ getRoleName(item.fromUser.role) }}:&nbsp;</span>
                    <span>{{ item.fromUser.name }}</span>
                  </p>
                  <p v-if="item.fromContact">
                    <span class="small">{{ $t('contact') }}:&nbsp;</span>
                    <a v-if="item.fromContact.customer" :href="'/#/customer/'+item.fromContact.customer"
                       class="btn-link">
                      {{ item.fromContact.name }}
                    </a>
                    <span v-else>{{ item.fromContact.name }}</span>
                  </p>
                </div>
              </div>
            </td>
            <td>
              <p v-if="item.toNumber">
                <b v-if="item.direction === 'outbound'">
                  <a @click="makeCall(item.toNumber)" href="javascript:;" v-b-tooltip.hover.top="$t('clickToCall')">
                    <phone-display :phone="item.toNumber"  :disabled="true"/>
                  </a>
                </b>
                <b v-else>{{ item.toNumber }}</b>
              </p>
              <p class="small" v-if="item.toExt">
                {{ $t('ext') }}:&nbsp;<b>{{ item.toExt }}</b>
              </p>
              <p v-if="item.toUser">
                <span class="small">{{ $t('agent') }}:&nbsp;</span>
                <span>{{ item.toUser.name }}</span>
              </p>
              <p v-if="item.toContact">
                <span class="small">{{ $t('contact') }}:&nbsp;</span>
                <a v-if="item.toContact.customer" :href="'/#/customer/'+item.toContact.customer" class="btn-link">
                  {{ item.toContact.name }}
                </a>
                <span v-else>{{ item.toContact.name }}</span>
              </p>
            </td>
            <td>
              <p>
                <span class="small text-gray-999">{{ $t('startTime') }}:&nbsp;</span>
                <span>{{ item.startTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
              </p>
              <p v-if="item.answerTime">
                <span class="small text-gray-999">{{ $t('answerTime') }}:&nbsp;</span>
                <span>{{ item.answerTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
              </p>
              <p v-if="item.endTime">
                <span class="small text-gray-999">{{ $t('endTime') }}:&nbsp;</span>
                <span>{{ item.endTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
              </p>
              <p v-if="item.campaign">
                <span class="small text-gray-999">{{$t('callCampaign')}}: </span>
                <span class="small">{{ item.campaign.name }} </span>
              </p>
            </td>
            <td>
              <p>
                <span class="small">{{ $t('duration') }}:&nbsp;</span>
                <b class="text-success">{{ item.duration | callDuration }}</b>
                &nbsp;|&nbsp;<span class="small">{{ $t('billDuration') }}:&nbsp;</span>
                <b class="text-danger" v-if="item.recordingDuration && (item.recordingDuration < item.billDuration)">{{ item.recordingDuration | callDuration }}</b>
                <b class="text-danger" v-else>{{item.billDuration | callDuration}}</b>
              </p>
              <div class="dropdown" v-if="item.recording && item.recordingDownloaded">
                <button class="btn btn-light" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                  Ghi âm
                </button>
                <form class="dropdown-menu custom-dropdown-menu" aria-labelledby="dropdownMenuButton">
                  <recording-player :id="item._id"
                                :duration="item.recordingDuration && item.recordingDuration < item.billDuration ? item.recordingDuration : item.billDuration"/>
                </form>
              </div>
              <audio-quality-bar v-if="item.audioQuality" :vpMin="0" :vpNow="item.audioQuality" :vpMax="100"></audio-quality-bar>
              <p class="small">
                <span class="small" v-if="item.direction === 'inbound' && ((item.hangupCause === 'NORMAL_CLEARING' && item.toExt && item.toExt.length < 5) || item.hangupCause !== 'NORMAL_CLEARING' || !item.billDuration)">
                  <b class="text-danger">{{ $t('missCall') }}</b> |
                </span>
                <span v-if="item.billDuration >= 1">{{ $t('connectedSuccessfully') }} |</span>
                &nbsp;<span>{{ item.hangupCause }}</span>
              </p>
            </td>
            <td>
              <div v-if="item.transcripts && item.transcripts.length">
                <div :class='[item.transcripts.join(", ").length <= 120 || item.showMore ? "c-voice-text active" : "c-voice-text"]'>
                  <div class="c-voice-text__content">
                    <span class="small text-gray-999">{{ $t('content') }}: </span>
                    {{ item.transcripts.join(", ") }}
                  </div>
                  <div class="c-voice-text__btn" @click="item.showMore = !item.showMore" v-if='item.transcripts.join(", ").length > 120'>
                    <span class="c-voice-text__expand">{{$t('seeMore')}}</span>
                    <span class="c-voice-text__close">{{$t('collapse')}}</span>
                  </div>
                </div>
              </div>
              <p v-else-if="item.billDuration" class="text-gray-999">{{ $t('noTranscript') }}</p>
              <p v-else class="text-gray-999">{{ $t('fail') }}</p>
            </td>
          </tr>
          </tbody>
          <tbody v-else>
          <tr>
            <td colspan="4" class="text-center">{{ $t('noCallFound') }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="c-paging" v-if="itemCount > filterFormModel.pageSize">
        <b-pagination size="sm" class="pagination pagination-sm justify-content-center"
                      v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                      @input="load">
        </b-pagination>
      </div>
    </div>
</template>

<script>
import list from "../../mixins/List";
import PhoneDisplay from "../../components/PhoneDisplay";
import _ from "lodash";
import RecordingPlayer from "../../components/RecordingPlayer";
import AudioQualityBar from "@/views/history/AudioQualityBar";

export default {
  props: ['benhNhan'],
  mixins: [list],
  components: {
    PhoneDisplay, RecordingPlayer, AudioQualityBar,
  },
  data() {
    return {
      service: '/call',
      showRecording: false,
      recordingUrl: null,
      showForm: false,
      defaultFilterFormModel: {
        number: this.benhNhan.phone
      },
      contactLoading: true,
      contacts: {},
      directionOptions: [
        {text: this.$t('chooseCallDirection'), value: null},
        {text: this.$t('inbound'), value: 1},
        {text: this.$t('outbound'), value: 2},
        {text: this.$t('local'), value: 3},
      ]
    }
  },
  methods: {
    apply(data) {
      for (let i in data.docs) {
        data.docs[i].showMore = false;
      }
      this.items = data.docs;
      this.itemCount = data.totalDocs;
      this.firstItemOfPage = this.itemCount > 0 ? (this.filterFormModel.page - 1)  * this.filterFormModel.pageSize + 1 : 0;
      this.lastItemOfPage = (this.filterFormModel.page - 1)  * this.filterFormModel.pageSize + this.items.length;
    },
    makeCall(phone) {
      if (phone) {
        this.$bus.$emit('makeCall', phone);
      } else {
        this.$swal({
          text: this.$t('phoneNotSet'),
          icon: 'error'
        });
      }
    },
    listen(id) {
      this.loading = true;
      this.$service.get(`/call/${id}/recording`).then(response => {
        this.showRecording = true;
        this.recordingUrl = response.data.url;
        this.$refs.player.play();
      }).catch(error => {
        if (error.response.data.msg) {
          this.$swal({
            text: error.response.data.msg,
            icon: 'error'
          });
        }
      }).finally(() => {
        this.loading = false
      });
    },
    search: _.debounce(function () {
      this.filterFormModel.page = 1;
      this.load();
    }, 200),
    getRoleName(key) {
      if (['owner', 'supervisor', 'agent'].includes(key)) {
        return this.$t(key);
      }
      let role = this.$store.state.roles.find(r => r.key === key);
      if (role && role.name) {
        return role.name
      }
      return key
    }
  },
  computed: {
    groupIds() {
      if (this.$store.state.user.permissions.includes('callListGroup') && !this.$store.state.user.permissions.includes('callListAll')) {
        return this.$store.state.user.roleGroups
      }
      return null
    }
  }
}
</script>